import type {
  DraggableLocation,
  DragStart,
  DropResult,
} from "@hello-pangea/dnd";
import { DragDropContext } from "@hello-pangea/dnd";
import { Text } from "@mantine/core";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "src/components/shared/Button/Button";
import { usePostApi } from "src/hooks";
import { Flex } from "src/styled";

import useGetAllYachts from "../Yachts/useGetAllYachts";
import ColumnsSelector from "./ColumnsSelector/ColumnsSelector";
import {
  type Entities,
  type SelectedColumn,
  type User,
} from "./CrewTransfer.model";
import { getMaps } from "./CrewTransfer.utils";
import CrewTransferMenuHint from "./CrewTransferMenuHint";
import Column from "./dnd/Column";
import {
  multiSelectTo as multiSelect,
  mutliDragAwareReorder,
} from "./dnd/utils";

const getTasks = (entities: Entities, columnId: string): User[] =>
  entities.columns[columnId].userIds.map(
    (userId: string): User => entities.users[userId],
  );

const CrewTransferMenu = () => {
  const { post } = usePostApi();

  const [entities, setAllEntities] = useState<Entities>();
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [draggingUserId, setDraggingUserId] = useState<string | null>(null);
  const [selectedColumns, setSelectedColumns] = useState<SelectedColumn[]>([]);
  const { yachts } = useGetAllYachts();

  const fetchCrew = useCallback(
    async (yachtid: string) => {
      return await post({
        request: "get-settings",
        body: { params: { sect: "_userlist" }, _yachtid: yachtid },
      });
    },
    [post],
  );

  useEffect(() => {
    if (!yachts || yachts.length === 0) return;
    void (async () => {
      const crews = await Promise.all(
        yachts.map(async (yacht: any) => await fetchCrew(yacht.yid)),
      );

      setAllEntities(getMaps(crews));
    })();
  }, [yachts, fetchCrew]);

  const toggleSelection = (userId: string): void => {
    const selectedTaskIds: string[] = selectedUsers;
    const wasSelected: boolean = selectedTaskIds.includes(userId);

    const newUserIds: string[] = (() => {
      if (!wasSelected) return [userId];
      if (selectedTaskIds.length > 1) return [userId];
      return [];
    })();

    setSelectedUsers(newUserIds);
  };

  const toggleSelectionInGroup = (userId: string): void => {
    const index: number = selectedUsers.indexOf(userId);
    if (index === -1) {
      setSelectedUsers([...selectedUsers, userId]);
      return;
    }
    const shallow: string[] = [...selectedUsers];
    shallow.splice(index, 1);

    setSelectedUsers(shallow);
  };

  const multiSelectTo = (newUserId: string): void => {
    if (!entities) return;
    const updated: string[] | undefined | null = multiSelect(
      entities,
      selectedUsers,
      newUserId,
    );

    if (updated == null) return;

    setSelectedUsers(updated);
  };

  const unselectAll = (): void => setSelectedUsers([]);

  const onDragStart = (start: DragStart): void => {
    const selected: string | undefined | null = selectedUsers.find(
      (userId: string): boolean => userId === start.draggableId,
    );

    if (!selected) unselectAll();

    setDraggingUserId(start.draggableId);
  };

  const onDragEnd = (result: DropResult): void => {
    const {
      destination,
      source,
    }: {
      destination: DraggableLocation | undefined | null;
      source: DraggableLocation;
    } = result;

    if (!destination || result.reason === "CANCEL") {
      setDraggingUserId(null);
      return;
    }

    if (!entities) return;

    const processed = mutliDragAwareReorder({
      entities,
      selectedTaskIds: selectedUsers,
      source,
      destination,
    });

    setAllEntities(processed.entities);
    setDraggingUserId(null);
  };
  const shownEntities = useMemo(
    () =>
      ({
        ...entities,
        columnOrder:
          entities?.columnOrder.filter(el =>
            selectedColumns.find(selectedEl => selectedEl.id === el),
          ) ?? [],
      }) as Entities,
    [entities, selectedColumns],
  );

  const handleSave = () => {};

  if (!entities) return <div>Loading...</div>;

  return (
    <Fragment>
      <Flex justify="space-between" mt={10}>
        <ColumnsSelector
          selectedColumns={selectedColumns}
          setSelectedColumns={setSelectedColumns}
          entities={entities}
        />
        <Button variant="outline" onClick={handleSave}>
          Save
        </Button>
      </Flex>

      {shownEntities.columnOrder.length === 0 ? (
        <Text size="lg" style={{ textAlign: "center", marginTop: 100 }}>
          Select vessels to transfer crew between
        </Text>
      ) : (
        <div style={{ marginTop: 10 }}>
          <CrewTransferMenuHint
            showClearButton={selectedUsers.length > 0}
            onClearSelections={() => setSelectedUsers([])}
          />

          <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <div style={{ display: "flex", userSelect: "none", gap: "10px" }}>
              {shownEntities.columnOrder.map((columnId: string) => (
                <Column
                  column={shownEntities.columns[columnId]}
                  users={getTasks(shownEntities, columnId)}
                  selectedUsers={selectedUsers}
                  key={columnId}
                  draggingUserId={draggingUserId}
                  toggleSelection={toggleSelection}
                  toggleSelectionInGroup={toggleSelectionInGroup}
                  multiSelectTo={multiSelectTo}
                />
              ))}
            </div>
          </DragDropContext>
        </div>
      )}
    </Fragment>
  );
};
export default CrewTransferMenu;
